import {
  ArnoLifecycleStatus,
  ArnoMappingStatus,
  ArnoReferenceState,
  ArnoSourceType,
  ArnoTemplateSource,
  IArnoDecisionTemplate,
  IArnoOverview,
} from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Beschriftungen und abgeleitete Texte der ArnoTop-Verwaltung (#1245).
 *
 * Reine Funktionen ohne Oberfläche: was auf einem Chip steht und welche Farbe er
 * hat, ist die eigentliche Aussage dieser Seiten — ob eine Zuordnung von Hand
 * entschieden oder nur vorgeschlagen wurde, entscheidet darüber, ob jemand noch
 * hinsehen muss. Das gehört geprüft, nicht durchgeklickt.
 */

export type ChipColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export const MAPPING_STATUS_LABEL: Record<ArnoMappingStatus, string> = {
  CONFIRMED: 'Bestätigt',
  MANUAL: 'Von Hand gesetzt',
  IGNORED: 'Bewusst ohne Ziel',
  SUGGESTED: 'Vorschlag',
  UNMAPPED: 'Offen',
};

export const SOURCE_TYPE_LABEL: Record<ArnoSourceType, string> = {
  DOCVARIABLE: 'Dokumentvariable',
  REGISTER: 'Register',
  FIELD: 'Feld',
  TRANSACTION_TYPE: 'Rechtsgeschäftsart',
  BLOCK_REFERENCE: 'Bausteinverweis',
};

export const REFERENCE_STATE_LABEL: Record<ArnoReferenceState, string> = {
  open: 'Offen',
  resolved: 'Aufgelöst',
  ignored: 'Bewusst offen gelassen',
};

/** Diese drei Zustände stammen von einem Menschen und überleben jeden Import. */
export const HUMAN_STATUSES: ArnoMappingStatus[] = [
  'CONFIRMED',
  'MANUAL',
  'IGNORED',
];

export const isHumanStatus = (status: ArnoMappingStatus): boolean =>
  HUMAN_STATUSES.includes(status);

/**
 * Wie eine Zuordnung in der Liste aussieht.
 *
 * Grün ist nur, was ein Mensch entschieden hat. Ein automatischer Vorschlag ist
 * blau, nicht grün: er sieht aus wie ein Ergebnis, ist aber eine offene Frage.
 */
export const mappingStatusChip = (
  status: ArnoMappingStatus
): { label: string; color: ChipColor } => {
  switch (status) {
    case 'CONFIRMED':
    case 'MANUAL':
      return { label: MAPPING_STATUS_LABEL[status], color: 'success' };
    case 'IGNORED':
      return { label: MAPPING_STATUS_LABEL[status], color: 'default' };
    case 'SUGGESTED':
      return { label: MAPPING_STATUS_LABEL[status], color: 'info' };
    default:
      return { label: MAPPING_STATUS_LABEL.UNMAPPED, color: 'warning' };
  }
};

export const referenceStateChip = (
  state: ArnoReferenceState
): { label: string; color: ChipColor } => {
  switch (state) {
    case 'resolved':
      return { label: REFERENCE_STATE_LABEL.resolved, color: 'success' };
    case 'ignored':
      return { label: REFERENCE_STATE_LABEL.ignored, color: 'default' };
    default:
      return { label: REFERENCE_STATE_LABEL.open, color: 'warning' };
  }
};

/**
 * Zerlegt einen Legacy-Schlüssel `Kategorie/Bereich/Bausteinname`.
 *
 * Nur die ersten beiden Schrägstriche trennen: Bausteinnamen aus ArnoTop
 * enthalten selbst welche, und der Name gehört vollständig auf den Bildschirm.
 */
export const splitReferenceKey = (
  key: string
): { category: string; area: string; blockName: string } => {
  const first = key.indexOf('/');
  if (first < 0) {
    return { category: '', area: '', blockName: key };
  }
  const second = key.indexOf('/', first + 1);
  if (second < 0) {
    return {
      category: key.slice(0, first),
      area: '',
      blockName: key.slice(first + 1),
    };
  }
  return {
    category: key.slice(0, first),
    area: key.slice(first + 1, second),
    blockName: key.slice(second + 1),
  };
};

/** Der lesbare Teil eines Bausteinschlüssels — für schmale Spalten. */
export const blockLeafName = (blockKey: string): string => {
  const parts = blockKey.split('/');
  return parts[parts.length - 1] || blockKey;
};

/** Woher ein Entscheidungsbaum stammt. */
export const TEMPLATE_SOURCE_LABEL: Record<ArnoTemplateSource, string> = {
  ARNOTOP_IMPORTED: 'ArnoTop',
  DINO_NATIVE: 'DiNo',
};

export const LIFECYCLE_LABEL: Record<ArnoLifecycleStatus, string> = {
  DRAFT: 'Entwurf',
  ACTIVE: 'Aktiv',
  ARCHIVED: 'Archiviert',
};

/**
 * Herkunft als Chip.
 *
 * Neutral, nicht wertend: ein importierter Baum ist nicht schlechter als ein
 * eigener. Die Farbe trennt nur, wo Legacy-Begriffe zu erwarten sind.
 */
export const templateSourceChip = (
  source: ArnoTemplateSource
): { label: string; color: ChipColor } =>
  source === 'DINO_NATIVE'
    ? { label: TEMPLATE_SOURCE_LABEL.DINO_NATIVE, color: 'primary' }
    : { label: TEMPLATE_SOURCE_LABEL.ARNOTOP_IMPORTED, color: 'default' };

/**
 * Lebenszyklus als Chip.
 *
 * Ein Entwurf wird der Urkundenerzeugung nicht angeboten — das ist eine
 * Aussage über die Benutzbarkeit und darf nicht wie ein Fehler aussehen.
 */
export const lifecycleChip = (
  status: ArnoLifecycleStatus
): { label: string; color: ChipColor } => {
  switch (status) {
    case 'ACTIVE':
      return { label: LIFECYCLE_LABEL.ACTIVE, color: 'success' };
    case 'ARCHIVED':
      return { label: LIFECYCLE_LABEL.ARCHIVED, color: 'default' };
    default:
      return { label: LIFECYCLE_LABEL.DRAFT, color: 'info' };
  }
};

/**
 * „12 von 37 Entscheidungsbäumen sind freigegeben."
 *
 * Freigegeben heißt: mindestens einer DiNo-Rechtsgeschäftsart zugeordnet. Erst
 * dann taucht der Baum bei einem Vorgang überhaupt auf.
 */
export const assignmentSummary = (
  templates: IArnoDecisionTemplate[]
): string => {
  if (templates.length === 0) {
    return 'Keine Entscheidungsbäume vorhanden.';
  }
  const released = templates.filter(item => item.enabledTypes > 0).length;
  return `${released} von ${templates.length} Entscheidungsbäumen freigegeben`;
};

/** Was in der Spalte „Zugeordnete DiNo-Arten" steht. */
export const assignedTypesLabel = (template: IArnoDecisionTemplate): string => {
  if (template.assignedTypes === 0) {
    return 'keine';
  }
  const disabled = template.assignedTypes - template.enabledTypes;
  // Eine bloße Zahl sagte nicht, wovon (»Freigegeben für: 1«, Sichtprüfung Teil 40). Bei genau
  // einer Freigabe steht deren Name da, sonst die Anzahl mit Einheit.
  const einzige =
    template.assignedTypes === 1
      ? template.assignments?.[0]?.legalTransactionType
      : null;
  const base = einzige
    ? einzige
    : `${template.assignedTypes} ${template.assignedTypes === 1 ? 'Rechtsgeschäftsart' : 'Rechtsgeschäftsarten'}`;
  return disabled > 0 ? `${base} (${disabled} ausgesetzt)` : base;
};

/**
 * Welche Version gilt.
 *
 * Ein importierter Baum lebt in der Versionsmenge des aktiven Importlaufs, ein
 * eigener zeigt selbst auf seine veröffentlichte Version. Ein Entwurf hat noch
 * keine — das gehört dort hin, wo sonst die Versionsnummer steht, sonst sieht
 * eine leere Zelle wie ein Fehler aus.
 */
export const versionLabel = (template: IArnoDecisionTemplate): string => {
  if (template.versionNumber) {
    return `Version ${template.versionNumber}`;
  }
  return template.latestVersionNumber
    ? `Entwurf, Version ${template.latestVersionNumber} unveröffentlicht`
    : 'ohne Version';
};

/** Auswahlstand im Freigabedialog — bei hunderten Einträgen die einzige Orientierung. */
export const selectionSummary = (selected: number, total: number): string => {
  if (total === 0) {
    return 'Keine Rechtsgeschäftsarten vorhanden.';
  }
  return selected === 0
    ? `Keine von ${total} ausgewählt`
    : `${selected} von ${total} ausgewählt`;
};

/** Zuordnungen, die noch niemand entschieden hat. */
export const openMappingCount = (overview?: IArnoOverview): number => {
  if (!overview) {
    return 0;
  }
  return (overview.mappings.UNMAPPED ?? 0) + (overview.mappings.SUGGESTED ?? 0);
};

/**
 * Was im Kopf der Seite steht, wenn kein Modell aktiv ist.
 *
 * Ohne aktiven Lauf ist die ganze Verwaltung leer, und der Grund dafür gehört
 * an die erste Stelle — sonst sucht jemand den Fehler in der Oberfläche.
 */
export const modelStateMessage = (
  overview?: IArnoOverview
): { severity: 'success' | 'info' | 'warning'; text: string } => {
  if (!overview) {
    return { severity: 'info', text: 'Der Stand wird geladen …' };
  }
  if (!overview.activeRun) {
    return {
      severity: 'warning',
      text: overview.lastRun
        ? `Kein aktives Modell. Der letzte Importlauf (#${overview.lastRun.idArnoImportRun}) steht auf ${overview.lastRun.status}.`
        : 'Kein aktives Modell und noch kein Importlauf vorhanden.',
    };
  }
  return {
    severity: 'success',
    text: `Aktives Modell: Lauf #${overview.activeRun.idArnoImportRun} mit ${overview.templates} Rechtsgeschäftsarten und ${overview.blocks} Bausteinen.`,
  };
};

/** Datum/Uhrzeit wie überall in DiNo, mit Gedankenstrich statt leerer Zelle. */
export const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '–';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('de-DE');
};

/**
 * Warum ein Speichern nicht gehen kann — als Text am ausgegrauten Knopf.
 *
 * `null` heißt: es spricht nichts dagegen.
 */
export const decisionBlockReason = (
  status: 'CONFIRMED' | 'MANUAL' | 'IGNORED',
  targetPath: string
): string | null => {
  if (status === 'IGNORED') {
    return null;
  }
  return targetPath.trim() ? null : 'Bitte zuerst ein DiNo-Zielfeld auswählen.';
};
